import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Link2, Clock, Ban, Search } from "lucide-react";
import { fmtEur, fmtData } from "@/lib/banche";

const Candidato = ({ c, sel, onToggle, onQuota }) => (
  <div className={`rounded-md border p-2 text-xs ${sel ? "border-primary bg-primary/5" : ""}`} data-testid={`coll-cand-${c.id}`}>
    <label className="flex items-start gap-2 cursor-pointer">
      <input type="checkbox" className="mt-0.5 h-4 w-4" checked={!!sel} onChange={() => onToggle(c)} data-testid={`coll-check-${c.id}`} />
      <div className="min-w-0 flex-1">
        <div className="flex justify-between gap-2"><span className="font-semibold truncate">n. {c.numero} · {c.cliente}</span><span className="tabular-nums">residuo <strong>{fmtEur(c.residuo)}</strong></span></div>
        <div className="text-muted-foreground">{fmtData(c.data)} · totale {fmtEur(c.totale)}{c.certo && <span className="text-emerald-700"> · corrispondenza</span>}</div>
      </div>
    </label>
    {sel && (
      <Input type="number" step="0.01" inputMode="decimal" className="h-8 mt-1" placeholder="Quota (vuoto = automatica)" value={sel.importo ?? ""} onChange={(e) => onQuota(c.id, e.target.value)} data-testid={`coll-quota-${c.id}`} />
    )}
  </div>
);

export const CollegaDialog = ({ mov, onClose, onDone }) => {
  const [mode, setMode] = useState(null);
  const [cand, setCand] = useState([]);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState({});
  const [nota, setNota] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { setMode(null); setSel({}); setQ(""); setNota(mov?.nota || ""); }, [mov]);
  useEffect(() => {
    if (!mov || mode !== "fattura") return;
    const t = setTimeout(() => {
      api.get(`/banche/movimenti/${mov.id}/proposte`, { params: { q } }).then(({ data }) => setCand(data.candidati)).catch(() => setCand([]));
    }, 300);
    return () => clearTimeout(t);
  }, [mov, mode, q]);
  if (!mov) return null;

  const tot = Math.abs(mov.importo);
  const toggle = (c) => setSel((s) => { const n = { ...s }; if (n[c.id]) delete n[c.id]; else n[c.id] = { id: c.id, residuo: c.residuo, data: c.data, importo: "" }; return n; });
  const quota = (id, v) => setSel((s) => ({ ...s, [id]: { ...s[id], importo: v } }));
  const scelte = Object.values(sel).sort((a, b) => (a.data > b.data ? 1 : -1));
  let rest = tot;
  const coperto = scelte.reduce((acc, s) => { const v = Math.min(rest, s.residuo, s.importo ? Number(s.importo) : s.residuo); rest -= v; return acc + Math.max(0, v); }, 0);

  const run = async (fn) => { setBusy(true); try { await fn(); onDone?.(); onClose(); } catch (e) { toast.error(e?.response?.data?.detail || "Operazione non riuscita"); } finally { setBusy(false); } };
  const collega = () => run(async () => { await api.post(`/banche/movimenti/${mov.id}/collega`, { fatture: scelte.map((s) => ({ id: s.id, importo: s.importo ? Number(s.importo) : null })) }); toast.success("Movimento collegato"); });
  const stato = (s) => run(async () => { await api.post(`/banche/movimenti/${mov.id}/stato`, { stato: s, nota }); toast.success("Movimento aggiornato"); });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto overflow-x-hidden" data-testid="collega-dialog">
        <DialogHeader><DialogTitle>Collega movimento</DialogTitle></DialogHeader>
        <div className="rounded-md bg-muted/50 p-3 text-sm">
          <div className="flex justify-between gap-2"><span>{fmtData(mov.data)} · {mov.banca}</span><span className={`font-bold ${mov.importo > 0 ? "text-emerald-700" : "text-red-600"}`}>{fmtEur(mov.importo)}</span></div>
          <div className="text-xs text-muted-foreground break-words">{mov.descrizione}</div>
        </div>
        {!mode && (
          <div className="grid gap-2">
            <Button className="h-12 justify-start" onClick={() => setMode("fattura")} data-testid="coll-mode-fattura"><Link2 className="h-4 w-4 mr-2" />Collega a una fattura {mov.importo > 0 ? "di vendita" : "di acquisto"}</Button>
            <Button variant="outline" className="h-12 justify-start" onClick={() => setMode("attesa")} data-testid="coll-mode-attesa"><Clock className="h-4 w-4 mr-2" />Inserisco la fattura dopo</Button>
            <Button variant="outline" className="h-12 justify-start" onClick={() => setMode("senza")} data-testid="coll-mode-senza"><Ban className="h-4 w-4 mr-2" />Non c'è fattura</Button>
          </div>
        )}
        {mode === "fattura" && (
          <div className="space-y-2">
            <div className="relative"><Search className="h-4 w-4 absolute left-2 top-2.5 text-muted-foreground" /><Input className="pl-8" placeholder="Cerca per numero o nome…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="coll-search" /></div>
            <div className="space-y-1.5 max-h-[45vh] overflow-y-auto" data-testid="coll-candidati">
              {cand.length === 0 && <div className="text-xs text-muted-foreground p-2">Nessuna fattura compatibile. Cerca per numero o nome.</div>}
              {cand.map((c) => <Candidato key={c.id} c={c} sel={sel[c.id]} onToggle={toggle} onQuota={quota} />)}
            </div>
            <div className="text-sm flex justify-between" data-testid="coll-somma">
              <span>Coperto {fmtEur(coperto)} su {fmtEur(tot)}</span>
              {tot - coperto > 0.005 && scelte.length > 0 && <span className="text-amber-700">eccedenza {fmtEur(tot - coperto)}</span>}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setMode(null)}>Indietro</Button>
              <Button className="flex-1 h-11" disabled={!scelte.length || busy} onClick={collega} data-testid="coll-conferma">{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Collega {scelte.length || ""} fattur{scelte.length === 1 ? "a" : "e"}</Button>
            </div>
          </div>
        )}
        {(mode === "attesa" || mode === "senza") && (
          <div className="space-y-2">
            <div className="text-sm text-muted-foreground">{mode === "attesa" ? "Ogni volta che registri o importi una fattura l'app riprova a collegare questo movimento." : "Es. commissioni, stipendi, F24, giroconti."}</div>
            <Input placeholder="Nota (facoltativa)" value={nota} onChange={(e) => setNota(e.target.value)} data-testid="coll-nota" />
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setMode(null)}>Indietro</Button>
              <Button className="flex-1 h-11" disabled={busy} onClick={() => stato(mode === "attesa" ? "attesa_fattura" : "senza_fattura")} data-testid="coll-stato-conferma">Conferma</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
